import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BarChart3, Users, Eye, Repeat, MousePointerClick } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { AudienceSnippet } from "../components/AudienceSnippet";
import { AudienceVisitors } from "../components/AudienceVisitors";

const SITES = [["all", "Les deux sites"], ["prime", "solutions-prime.fr"], ["xaltix", "solutions-prime.fr/xaltix"]];
const PERIODS = [[7, "7 jours"], [30, "30 jours"], [90, "90 jours"], [365, "12 mois"]];
const pill = (on) => `px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${on ? "bg-[#0B2A7A] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`;

const Kpi = ({ icon: I, label, value, sub, id }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`aud-kpi-${id}`}>
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500"><I className="h-4 w-4 text-[#C8922E]" />{label}</div>
    <div className="font-display text-3xl font-bold mt-1 text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const TopList = ({ title, rows, id }) => {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`aud-top-${id}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">{title}</div>
      {rows.length === 0 ? <p className="text-sm text-slate-400">Aucune donnée</p> : rows.map((r) => (
        <div key={r.label} className="mb-2">
          <div className="flex justify-between text-sm"><span className="truncate pr-2 text-slate-700">{r.label}</span><b className="text-slate-900">{r.n}</b></div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1"><div className="h-1.5 rounded-full bg-[#D9A441]" style={{ width: `${(100 * r.n) / max}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

export default function SuperAudience() {
  const [site, setSite] = useState("all");
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setErr("");
    http.get("/superadmin/web-stats", { params: { site, days } }).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [site, days]);
  return (
    <div className="p-8 max-w-[1800px] mx-auto space-y-6" data-testid="audience-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><BarChart3 className="h-8 w-8 text-[#C8922E]" />Audience des sites</h1>
          <p className="text-sm text-slate-500 mt-1">Visites de solutions-prime.fr et de la page XALTIX, provenance et visiteurs qui reviennent.</p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <div className="flex gap-1.5">{SITES.map(([k, l]) => <button key={k} onClick={() => setSite(k)} className={pill(site === k)} data-testid={`aud-site-${k}`}>{l}</button>)}</div>
          <div className="flex gap-1.5">{PERIODS.map(([k, l]) => <button key={k} onClick={() => setDays(k)} className={pill(days === k)} data-testid={`aud-days-${k}`}>{l}</button>)}</div>
        </div>
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-4 py-3" data-testid="aud-error">{err}</div>}
      {d && <>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Kpi id="visits" icon={MousePointerClick} label="Visites" value={d.visits} sub={`${d.pages_per_visit} page(s) par visite`} />
          <Kpi id="visitors" icon={Users} label="Visiteurs uniques" value={d.visitors} />
          <Kpi id="pageviews" icon={Eye} label="Pages vues" value={d.pageviews} />
          <Kpi id="returning" icon={Repeat} label="Visiteurs revenus" value={d.returning} sub={`sur ${d.known_visitors} reconnus`} />
          <Kpi id="consent" icon={Users} label="Cookies acceptés" value={`${d.consent_rate} %`} sub="des visites" />
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="aud-chart">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Visites et visiteurs par jour</div>
          <div className="h-56"><ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.series} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
              <XAxis dataKey="day" tickFormatter={(v) => v.slice(8, 10) + "/" + v.slice(5, 7)} tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString("fr-FR")} />
              <Area type="monotone" dataKey="visits" name="Visites" stroke="#0B2A7A" fill="#0B2A7A" fillOpacity={0.15} />
              <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke="#C8922E" fill="#D9A441" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer></div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <TopList id="sources" title="Provenance" rows={d.sources} />
          <TopList id="pages" title="Pages les plus vues" rows={d.pages} />
          <TopList id="frequency" title="Fréquence des visiteurs reconnus" rows={d.frequency} />
          <TopList id="devices" title="Appareils" rows={d.devices} />
          <TopList id="countries" title="Pays" rows={d.countries} />
          <TopList id="sites" title="Répartition par site" rows={d.by_site.map((s) => ({ label: s.label, n: s.visits }))} />
        </div>
        <AudienceVisitors rows={d.top_visitors} />
      </>}
      <AudienceSnippet />
    </div>
  );
}
